import { useCallback, useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase";

export type MemberRole = "super_admin" | "admin" | "staff";

export interface Company {
  id: string;
  slug: string;
  name: string;
  displayName: string | null;
  timezone: string | null;
  brandHex: string | null;
  /** Days recorded audio is kept. null means indefinitely — a choice, not an absence. */
  retentionDays: number | null;
  role: MemberRole;
  modules: string[];
}

export function useSession() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  return { session, loading };
}

/**
 * The companies this user may see. We ask the database rather than reading
 * anything from the token: a token minted before someone was removed from a
 * company would otherwise keep working until it expired.
 */
export function useCompanies(session: Session | null) {
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!session) {
      setCompanies(null);
      return;
    }
    const { data, error } = await supabase
      .from("memberships")
      .select("role, companies!inner(id, slug, name, display_name, timezone, brand_hex, recording_retention_days, company_modules(module_key, enabled))")
      .eq("user_id", session.user.id)
      .eq("status", "active");

    if (error) {
      setError(error.message);
      setCompanies([]);
      return;
    }

    type Row = {
      role: MemberRole;
      companies: {
        id: string;
        slug: string;
        name: string;
        display_name: string | null;
        timezone: string | null;
        brand_hex: string | null;
        recording_retention_days: number | null;
        company_modules: { module_key: string; enabled: boolean }[];
      };
    };

    setCompanies(
      (data as unknown as Row[]).map((row) => ({
        id: row.companies.id,
        slug: row.companies.slug,
        name: row.companies.name,
        displayName: row.companies.display_name,
        timezone: row.companies.timezone,
        brandHex: row.companies.brand_hex,
        retentionDays: row.companies.recording_retention_days,
        role: row.role,
        modules: row.companies.company_modules.filter((m) => m.enabled).map((m) => m.module_key),
      })),
    );
  }, [session]);

  useEffect(() => {
    void load();
  }, [load]);

  return { companies, error, reload: load };
}

/** Navigation is derived from entitlements, so an unowned module never renders. */
export const MODULE_NAV: { key: string; label: string; path: string }[] = [
  { key: "call_tracking", label: "Calls", path: "/calls" },
  { key: "crm", label: "Contacts", path: "/contacts" },
  { key: "bulk_sms", label: "Inbox", path: "/inbox" },
  { key: "reviews", label: "Reviews", path: "/reviews" },
  { key: "local_seo", label: "Rankings", path: "/rankings" },
  { key: "website", label: "Website", path: "/website" },
];
